// Points at the self-hosted survivor-api service (server/index.js) — direct
// to its port during `npm run dev`, through melodia-server's /survivor-api
// proxy route in production (same origin as the game itself, which is
// served at melodia.flowokr.com/survivor/ — see ../../../../melodia-server/server.js).
const API_BASE = import.meta.env.DEV ? 'http://127.0.0.1:8291' : 'https://melodia.flowokr.com/survivor-api';

/**
 * Thin fetch wrapper around the leaderboard API. Every method is wrapped in
 * try/catch and never throws into the game — the leaderboard is a nice-to-
 * have on top of a fully playable offline game, so a down/unreachable
 * server degrades to an empty list / a failed submit, not a crash.
 */
export const LeaderboardClient = {
  // `board` picks the ladder: one per act — 'act1' (which Endless also posts
  // to, since it loops Act I), 'act2', 'act3' — plus 'campaign', the legacy
  // board holding the thirteen-boss rows earned before the acts were split.
  // Separate boards rather than a filter because the score's progress term is
  // start-relative and each act begins at a different index — see data/acts.js.
  // `seed` (BULLET-HELL.md §D8) narrows a board to the runs that played one
  // seed — the daily view. A filter here rather than a board of its own,
  // unlike the acts above: a seeded Act I run starts where every other Act I run
  // starts, so its score means the same thing and belongs on the same ladder.
  // Absent means every run on the board, seeded or not, exactly as before.
  async getTop({ limit = 50, category = 'all', ascension = null, board = 'act1', seed = null } = {}) {
    try {
      const params = new URLSearchParams({ limit: String(limit), category, board });
      if (ascension !== null && ascension !== '') params.set('ascension', String(ascension));
      if (seed) params.set('seed', seed);
      const res = await fetch(`${API_BASE}/scores?${params}`);
      if (!res.ok) return { ok: false, scores: [] };
      const data = await res.json();
      return { ok: true, scores: Array.isArray(data.scores) ? data.scores : [] };
    } catch {
      return { ok: false, scores: [] };
    }
  },

  async submit(entry) {
    try {
      const res = await fetch(`${API_BASE}/scores`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(entry),
      });
      if (!res.ok) return { ok: false };
      const data = await res.json();
      return { ok: true, rank: data.rank ?? null };
    } catch {
      return { ok: false };
    }
  },
};
